import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const CACHE_KEY = 'gc_branding_cache_v1';

const DEFAULT_BRANDING = {
  organisationName: 'GetCare 360',
  logoUrl: '',
  faviconUrl: '',
  loginLogoUrl: '',
  primaryColour: '#16A34A',
  secondaryColour: '#0EA5E9',
  loaded: false,
};

function readCachedBranding() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (cached && typeof cached === 'object' && cached.organisationName) {
      return { ...DEFAULT_BRANDING, ...cached, loaded: false };
    }
  } catch {
    // ignore
  }
  return null;
}

function writeCachedBranding(branding) {
  try {
    const { loaded: _ignored, ...persistable } = branding;
    localStorage.setItem(CACHE_KEY, JSON.stringify(persistable));
  } catch {
    // ignore quota errors
  }
}

const BrandingContext = createContext({
  ...DEFAULT_BRANDING,
  refresh: () => {},
});

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ──── Colour utilities ────

function hexToHsl(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslString(h, s, l) {
  return `${h} ${s}% ${l}%`;
}

function applyBrandingTheme(primaryHex, secondaryHex) {
  const root = document.documentElement;
  const p = hexToHsl(primaryHex);
  const s = hexToHsl(secondaryHex);

  // Primary colour tokens (buttons, active states, links, focus ring)
  root.style.setProperty('--primary', hslString(p.h, p.s, p.l));
  root.style.setProperty('--primary-foreground', p.l > 55 ? '215 20% 13%' : '0 0% 100%');
  root.style.setProperty('--ring', hslString(p.h, p.s, p.l));

  // Accent colour (sidebar hover, secondary highlights)
  root.style.setProperty('--accent', hslString(p.h, Math.max(p.s - 25, 10), 92));
  root.style.setProperty('--accent-foreground', hslString(p.h, p.s, Math.max(p.l - 8, 15)));

  // Custom brand CSS vars
  root.style.setProperty('--brand-primary', primaryHex);
  root.style.setProperty('--brand-primary-hover', hslString(p.h, p.s, Math.max(p.l - 8, 10)));
  root.style.setProperty('--brand-primary-active', hslString(p.h, p.s, Math.max(p.l - 14, 8)));

  root.style.setProperty('--brand-secondary', secondaryHex);
  root.style.setProperty('--brand-secondary-hover', hslString(s.h, s.s, Math.max(s.l - 8, 10)));
  root.style.setProperty('--brand-secondary-active', hslString(s.h, s.s, Math.max(s.l - 14, 8)));

  // GC custom tokens
  root.style.setProperty('--gc-teal', hslString(p.h, p.s, p.l));
  root.style.setProperty('--gc-teal-2', hslString(p.h, Math.max(p.s - 15, 20), Math.min(p.l + 10, 50)));
}

// ──── Provider ────

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => {
    // Hydrate immediately from cache so we don't flash the default 'GetCare 360'
    // between mount and the first /admin/settings/branding response.
    const cached = readCachedBranding();
    if (cached) {
      // Apply cached theme tokens synchronously before first paint.
      try {
        applyBrandingTheme(cached.primaryColour, cached.secondaryColour);
        if (cached.organisationName) document.title = cached.organisationName;
      } catch {
        // ignore — server-side render safety
      }
      return cached;
    }
    return { ...DEFAULT_BRANDING };
  });

  const loadBranding = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      let d;
      if (token) {
        const res = await api.get('/admin/settings/branding');
        d = res.data.data;
      } else {
        // Public endpoint for login page
        const res = await api.get('/admin/settings/branding/public');
        d = res.data.data;
      }
      const newBranding = {
        organisationName: d.organisation_name || 'GetCare 360',
        logoUrl: d.logo_url ? `${API_BASE}${d.logo_url}` : '',
        faviconUrl: d.favicon_url ? `${API_BASE}${d.favicon_url}` : '',
        loginLogoUrl: d.login_logo_url ? `${API_BASE}${d.login_logo_url}` : '',
        primaryColour: d.primary_colour || '#16A34A',
        secondaryColour: d.secondary_colour || '#0EA5E9',
        loaded: true,
      };
      setBranding(newBranding);
      writeCachedBranding(newBranding);

      // Apply full theme to CSS
      applyBrandingTheme(newBranding.primaryColour, newBranding.secondaryColour);

      // Update favicon
      if (newBranding.faviconUrl) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.rel = 'icon';
        link.href = newBranding.faviconUrl;
        document.head.appendChild(link);
      }

      // Update page title
      document.title = newBranding.organisationName;
    } catch {
      setBranding(b => ({ ...b, loaded: true }));
    }
  }, []);

  useEffect(() => { loadBranding(); }, [loadBranding]);

  const contextValue = { ...branding, refresh: loadBranding };

  return (
    <BrandingContext.Provider value={contextValue}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export { applyBrandingTheme };
